import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import OTPInput from "./OTPInput"
import TextField from "../textField"
import localStorageService from "../../services/localStorage.service"
import authService from "../../services/auth.service"
import { useDispatch } from "react-redux"
import { loginClient } from "../../store/authSlice"
import dictionary from "../../utils/dictionary"
import { useSelector } from "react-redux"
import useRecaptchaToken from "../../hooks/useRecaptchaToken"
import { prefetchClientData } from "../../hooks/useApiQueries"

function getPhoneValidationError(phone) {
  if (!phone || phone === "+") return null
  if (phone.startsWith("+3")) {
    if (!/^\+3\d{10}$/.test(phone) && !/^\+3\d{11}$/.test(phone)) {
      return "phone must contain 11-12 digits"
    }
    return null
  }
  if (phone.startsWith("+7")) {
    if (!/^\+7\d{10}$/.test(phone)) {
      return "phone must contain 11 digits"
    }
    return null
  }
  if (phone.length > 1) {
    return "phone must start with '7' or '3'"
  }
  return null
}

function isPhoneComplete(phone) {
  if (!phone || phone.length < 4) return false
  return !getPhoneValidationError(phone)
}

function isAxiosGenericMessage(message) {
  return /^Request failed with status code \d+$/.test(message || "")
}

function formatClientLoginError(error, t) {
  const status = error?.response?.status
  const detail = error?.response?.data?.detail
  const code = typeof detail === "object" ? detail?.code : null
  const serverMessage =
    (typeof detail === "object" && detail?.message) ||
    (typeof detail === "string" ? detail : null)

  if (serverMessage?.includes("reCAPTCHA")) {
    return t.clientLoginRecaptchaFailed
  }
  if (code === "company_slug_required") {
    return t.clientLoginCompanySlugRequired
  }
  if (
    status === 503 ||
    code === "sms_insufficient_credit" ||
    code === "sms_delivery_failed"
  ) {
    return t.clientLoginSmsFailed
  }
  if (status === 429 || code === "rate_limit_exceeded") {
    return t.clientLoginRateLimit
  }
  if (status === 401 || code === "invalid_credentials") {
    return t.clientLoginInvalidCredentials
  }
  if (serverMessage && !isAxiosGenericMessage(serverMessage)) {
    return serverMessage
  }
  return t.clientLoginFailed
}

const ClientLoginForm = () => {
  const [otp, setOtp] = useState("")
  const [stage, setStage] = useState(false)
  const navigate = useNavigate()
  const [data, setData] = useState({
    phone: "+",
    code: "",
  })
  const [submitError, setSubmitError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const selectedLanguage = useSelector((state) => state.lang.lang)
  const t = dictionary[selectedLanguage]
  const { ready, getToken } = useRecaptchaToken()

  const phoneError = getPhoneValidationError(data.phone)
  const phoneComplete = isPhoneComplete(data.phone)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError("")

    if (!stage && !phoneComplete) {
      setSubmitError(t.clientLoginPhoneIncomplete)
      return
    }

    if (!stage && !ready) {
      setSubmitError(t.clientLoginRecaptchaLoading)
      return
    }

    setIsSubmitting(true)
    try {
      if (!stage) {
        const recaptchaToken = await getToken("client_auth_code")
        const { phone } = await authService.getClientAuthCode(
          data.phone,
          recaptchaToken
        )
        localStorageService.setClientPhone(phone)
        setStage(true)
      } else {
        const phone = localStorageService.getClientPhone()
        const authData = await authService.clientLogin(phone, otp)
        if (authData) {
          localStorageService.setClientTokens({ ...authData })
          await prefetchClientData(queryClient, authData.clientId)
          dispatch(loginClient())
          navigate("/client")
        }
      }
    } catch (error) {
      console.error(error)
      setSubmitError(formatClientLoginError(error, t))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (target) => {
    setSubmitError("")
    if (target.name === "phone") {
      if (!data.phone.startsWith("+")) {
        setData((prevState) => ({
          ...prevState,
          [target.name]: "+" + target.value,
        }))
      } else {
        setData((prevState) => ({
          ...prevState,
          [target.name]: target.value,
        }))
      }
    } else {
      setData((prevState) => ({
        ...prevState,
        [target.name]: target.value,
      }))
    }
  }

  const submitDisabled = stage
    ? isSubmitting || otp.length < 5
    : isSubmitting || Boolean(phoneError) || !phoneComplete || !ready

  return (
    <form onSubmit={handleSubmit}>
      {stage ? (
        <>
          <p className="text-brown mb-[4px] text-2xl max-md:text-lg">
            {t.confirm}
          </p>
          <p className="text-lightBrown mb-[24px] text-sm drop-shadow-2xl">
            {t.smsCode}
          </p>
          <OTPInput
            length={5}
            className="mx-auto mb-[24px] flex justify-between"
            inputClassName="w-[65px] h-[65px] max-md:w-[50px] max-md:h-[50px] border border-solid border-brown text-brown rounded-lg bg-cream text-center outline-none focus:border-2"
            isNumberInput
            autoFocus
            onChangeOTP={(otp) => setOtp(otp)}
          />
          <button
            className="bg-brown rounded-lg text-white text-center w-full py-[8px] mt-[24px] hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-60"
            type="submit"
            disabled={submitDisabled}
          >
            {isSubmitting ? t.clientLoginChecking : t.confirm}
          </button>
        </>
      ) : (
        <>
          <p className="text-brown mb-[4px] text-2xl max-md:text-lg">
            {t.enter}
          </p>
          <p className="text-lightBrown mb-[24px] text-sm drop-shadow-2xl">
            {t.phoneToLogin}
          </p>
          <TextField
            name="phone"
            label={t.phoneNumber}
            placeholder="+1234567890"
            type="text"
            value={data.phone}
            onChange={handleChange}
            error={phoneError}
          />
          <button
            className="bg-brown rounded-lg text-white text-center w-full py-[8px] mt-[24px] hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-60"
            type="submit"
            disabled={submitDisabled}
          >
            {isSubmitting ? t.clientLoginSending : t.login}
          </button>
        </>
      )}
      {submitError ? (
        <p className="mt-4 text-sm text-red" role="alert">
          {submitError}
        </p>
      ) : null}
    </form>
  )
}

export default ClientLoginForm
